import { useEffect } from "react";
import type { FieldMappingOption } from "@/types/workflow";
import type { AppName } from "@/constants/application/application";
import { z } from "zod"
import { BackButton } from "@/components/custom/BackButton";
import { PageTitle } from "@/components/custom/PageTitle";
import { AppDispatch, RootState } from "@/redux";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Form } from "@/components/ui/form"
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { WorkflowCreateUpdateSchemaV2 } from "@/constants/workflows/workflows";
import { Section } from "./components/Section";
import { TextInputField } from "@/components/custom/InputField/TextInputField";
import { WorkflowExecution } from "./components/WorkflowExecution";
import { getFormProps } from "@/helpers/formHelpers";
import { InputFieldWrapper } from "./components/InputFieldsSection";
import { WorkflowRunType, WorkflowStepType } from "@/constants/workflows/workflowCreate";
import { convertQuartzCronToUTC, convertToUTC, encodeCronMetadata } from "@/helpers/date";
import { StepTask } from "./components/wfStep/Step";
import { SelectTaskApplication } from "./components/Modals/SelectTaskApplication";
import { Button } from "@/components/ui/button";
import { ButtonVariant } from "@/constants/button/button";
import { hasValidApplications, hasValidFieldMappings, isValidActionsSubjectsAndCredentials } from "../helpers/formValidation";
import { TwoAppFieldMapping, WFCreateStep, WorkflowCreateRequestBodyV2 } from "@/types/WorkflowCreate/workflowCreate";
import { extractStepNumber } from "./helpers/getStepOrderFromString";
import { Settings } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { workflowActions } from "@/redux/slice/workflowSlice";
import { StepTransformation } from "./components/wfStep/Transformation";
import { createWorkflow, saveDraftWorkflow } from "@/services/workflowService";
import { normalizeWorkflowRequestBody } from "./helpers/normalizeWorkflowRequest";
import { isActiveSubscription } from "@/helpers/billing";

enum SubmitType {
  DRAFT = 'draft',
  PUBLISH = 'publish'
}

export const CreateWorkflowV2 = () => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { token } = useSelector((state: RootState) => state.user)
  const { applications } = useSelector((state: RootState) => state.application)
  const { create } = useSelector((state: RootState) => state.workflow)
  const billingSubscription = useSelector((state: RootState) => state.billing?.subscription)

  useEffect(() => {
    if (billingSubscription !== undefined && !isActiveSubscription(billingSubscription)) {
      navigate('/billing')
    }
  }, [billingSubscription, navigate])

  const form = useForm<z.infer<typeof WorkflowCreateUpdateSchemaV2>>({
    resolver: zodResolver(WorkflowCreateUpdateSchemaV2),
    defaultValues: {
      nickname: '',
    },
  })
  const formProps = getFormProps(form, WorkflowCreateUpdateSchemaV2)

  const buildCreateRequestBody = (
    values: z.infer<typeof WorkflowCreateUpdateSchemaV2>
  ): WorkflowCreateRequestBodyV2 => {
    const stepsByOrder = new Map<number, WFCreateStep>();
    create.steps.forEach((step) => stepsByOrder.set(step.order, step));

    const appIdSet = new Set<string>(
      create.steps
        .map((step) => step.id)
        .filter((id): id is string => Boolean(id))
    );

    const appNameToId = new Map<AppName, string>(applications.map((app) => [app.name, app.id] as const));

    type PreparedMapping = {
      sourceTaskOrder: number;
      destinationTaskOrder: number;
      sourceAppId: string | null;
      destinationAppId: string;
      iterateThrough?: string;
      options?: FieldMappingOption;
      order: number;
      sourceField: string;
      destinationField: string;
    };

    const preparedMappings = create.fieldMappings
      .flatMap((item) =>
        item.mappings.map((m) => {
          const [sourceAppWithStep, sourceFieldName] = m.dataSourceFieldId.split('=>');
          const [sourceTaskOrderRaw, sourceAppName] = sourceAppWithStep.split(';');
          const [destinationOrderRaw, destinationAppIdRaw] = item.destinationId.split(';');

          const sourceTaskOrder = extractStepNumber(sourceTaskOrderRaw);
          const destinationTaskOrder = extractStepNumber(destinationOrderRaw);

          if (sourceTaskOrder === null || destinationTaskOrder === null) {
            return null;
          }

          const sourceStep = stepsByOrder.get(sourceTaskOrder);
          const destinationStep = stepsByOrder.get(destinationTaskOrder);

          if (!destinationStep) {
            return null;
          }

          const isTransformationSource = sourceStep?.stepType === WorkflowStepType.TRANSFORMATION;
          const resolvedSourceAppId = isTransformationSource
            ? null
            : appNameToId.get(sourceAppName as AppName) ?? sourceStep?.id ?? null;

          if (!isTransformationSource && !resolvedSourceAppId) {
            return null;
          }

          const resolvedDestinationAppId = destinationStep.id ?? destinationAppIdRaw;

          if (!resolvedDestinationAppId) {
            return null;
          }

          const resolvedSourceField = m.customSourceLabel?.trim()?.length
            ? m.customSourceLabel.trim()
            : sourceFieldName;

          const mapping: PreparedMapping = {
            sourceTaskOrder,
            destinationTaskOrder,
            sourceAppId: resolvedSourceAppId,
            destinationAppId: resolvedDestinationAppId,
            iterateThrough: item.iterateThrough,
            options: m.options,
            order: m.order,
            sourceField: resolvedSourceField,
            destinationField: m.fieldName,
          };

          return mapping;
        })
      )
      .filter((mapping): mapping is PreparedMapping => mapping !== null);

    const aggregatedFieldMappings = preparedMappings.reduce<Record<string, TwoAppFieldMapping>>(
      (acc, mapping) => {
        const sourceKey = mapping.sourceAppId ?? `TRANSFORMATION_${mapping.sourceTaskOrder}`;
        const aggregateKey = `${sourceKey}_${mapping.sourceTaskOrder}_${mapping.destinationTaskOrder}_${mapping.iterateThrough ?? 'none'}_${mapping.destinationAppId}`;

        if (!acc[aggregateKey]) {
          acc[aggregateKey] = {
            sourceAppId: mapping.sourceAppId,
            destinationAppId: mapping.destinationAppId,
            iterateThrough: mapping.iterateThrough,
            sourceTaskOrder: mapping.sourceTaskOrder,
            destinationTaskOrder: mapping.destinationTaskOrder,
            mappings: [],
          };
        }

        acc[aggregateKey].mappings.push({
          sourceField: mapping.sourceField,
          destinationField: mapping.destinationField,
          options: mapping.options,
          order: mapping.order,
        });

        return acc;
      },
      {}
    );

    const fieldMappingsPayload = Object.values(aggregatedFieldMappings).filter((fm) => {
      const destinationExists = appIdSet.has(fm.destinationAppId);
      if (!destinationExists) {
        return false;
      }

      if (fm.sourceAppId) {
        return appIdSet.has(fm.sourceAppId);
      }

      return create.steps.some(
        (step) => step.order === fm.sourceTaskOrder && step.stepType === WorkflowStepType.TRANSFORMATION
      );
    });

    const requestBody: WorkflowCreateRequestBodyV2 = {
      nickname: values.nickname,
      workflowTasks: create.steps.map(step => ({
        appId: step.id,
        stepType: step.stepType,
        action: step.action,
        subject: step.subject,
        credentialIds: step.credentialId?.length ? [step.credentialId] : [],
        order: step.order,
        filters: step.filters,
        options: step.options
      })),
      startingFrom: create.runType === WorkflowRunType.SCHEDULED ?
        convertToUTC(
          create.executionStartDate,
          create.executionStartHour,
          create.executionStartMinute,
          create.amPm,
          create.timezone
        ) : undefined,
      workflowSchedule: create.runType === WorkflowRunType.SCHEDULED ? {
        // Store original cron in scheduleType as metadata for WYSIWYG display
        scheduleType: encodeCronMetadata(create.cron, create.timezone),
        timezone: create.timezone,
        cronExpression: convertQuartzCronToUTC(create.cron, create.timezone) // Backend executes in UTC
      } : undefined,
      fieldMappings: fieldMappingsPayload,
      transformationSteps: create.transformationConfigurations.map(ts => ({ ...ts, order: ts.taskOrder })),
    }

    return requestBody
  }

  const submitWorkflow = async (
    values: z.infer<typeof WorkflowCreateUpdateSchemaV2>,
    mode: SubmitType
  ) => {
    if (!token) return

    if (mode === SubmitType.DRAFT) {
      const draftBody = buildCreateRequestBody(values)
      const resp = await dispatch(saveDraftWorkflow(token, draftBody))
      if (resp.data) {
        navigate('/workflows')
        form.reset({
          nickname: "",
        })
      }
      return
    }

    const requestBody = normalizeWorkflowRequestBody(buildCreateRequestBody(values))

    if (
      isValidActionsSubjectsAndCredentials(requestBody.workflowTasks, applications) &&
      hasValidApplications(requestBody.workflowTasks, applications) &&
      hasValidFieldMappings(requestBody.fieldMappings, applications)
    ) {
      const resp = await dispatch(createWorkflow(token, requestBody))
      if (resp.data) {
        navigate('/workflows')
        form.reset({
          nickname: "",
        })
      }
    }
  }

  return (
    <div className="protectedPageLayout">
      <BackButton buttonLink="/workflows" buttonText="Back to Workflows" />
      <PageTitle title={'Create Workflow'} />
      <Form {...form}>
        <form
          id="edit-workflow"
          onSubmit={form.handleSubmit((values) => submitWorkflow(values, SubmitType.PUBLISH))}
          className="space-y-8 flex flex-col items-start"
        >
          <Section
            title=""
            subtitle="Workflow Configuration"
            description="Name your workflow and choose when your workflow gets triggered"
          >
            {/* NickName */}
            <InputFieldWrapper title="Give your workflow a nickname">
              <TextInputField
                name="nickname"
                label=""
                placeholder="Nickname"
                error={!!form.formState.errors.nickname}
                additionalClassName="w-full bg-background placeholderText"
                {...formProps}
              />
            </InputFieldWrapper>
            <div className="my-8" />
            {/* Workflow Execution */}
            <InputFieldWrapper title="How does your workflow start?">
              <WorkflowExecution />
            </InputFieldWrapper>
            <div className="my-8" />
          </Section>
          {create.steps.map((taskStep, i) => {
            const app = applications.find(app => app.id === taskStep.id)
            if (app) {
              return (
                <StepTask
                  key={i + app.id}
                  index={i}
                  wfStep={taskStep}
                />
              )
            } else if (!app && taskStep?.stepType === WorkflowStepType.TRANSFORMATION) {
              return (
                <StepTransformation
                  key={i}
                  index={i}
                  wfStep={taskStep}
                />
              )
            }
          })}
          <div className="w-full flex flex-col">
            <div className="flex flex-row items-center justify-end">
              <SelectTaskApplication applications={applications} />
              <div className="mr-4" />
              <Button
                type="button"
                className="font-semibold text-accent border-accent bg-background hover:text-accent hover:border-accent"
                variant={ButtonVariant.OUTLINE}
                onClick={() => {
                  const orders = create.steps
                    .map(s => s.order)
                    .filter((n): n is number => Number.isFinite(n));

                  // next order: 0 if none, else max + 1
                  const nextOrder = orders.length ? Math.max(...orders) + 1 : 0;

                  dispatch(workflowActions.addStep({
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    id: null as any,
                    stepType: WorkflowStepType.TRANSFORMATION,
                    action: '',
                    subject: 'data',
                    credentialId: '',
                    filters: [],
                    options: [],
                    order: nextOrder,
                    requiredOptions: []
                  }));
                }}
                disabled={false}
              >
                <Settings className="mr-2" size={16} />
                Add Transformation
              </Button>
            </div>
            <Separator className="my-6" />
            <div className="w-full flex flex-row justify-end">
              {/* TODO Uncomment the below line when working from hs-draft-wf-startover branch */}
              {/* <Button
                type='button'
                disabled={false}
                variant={ButtonVariant.OUTLINE}
                className='font-semibold text-accent border-accent bg-background hover:text-accent hover:border-accent'
                onClick={form.handleSubmit((values) => submitWorkflow(values, SubmitType.DRAFT))}
              >
                Save as Draft
              </Button> */}
              <div className="mr-4" />
              <Button
                type='submit'
                disabled={false}
                variant={ButtonVariant.DEFAULT}
                className='font-semibold'
              >
                Publish Workflow
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  )
}
